/**
 * Feature flags. Defaults may depend on the deployment (staging gets new surfaces first); a per-browser
 * override comes from `?flags=lanesTab` / `?flags=-lanesTab` and is remembered in localStorage.
 * Storage may be unavailable (private windows): the default then applies.
 */
export type Flag = 'lanesTab'

const KEY = 'viko.flags'
const FLAGS: readonly Flag[] = ['lanesTab']

/**
 * Deployment default. The tab shell (the tab strip is the navigation: Home, project tabs,
 * Settings) is on everywhere, production included; `?flags=-lanesTab` restores the old rail
 * shell for one release as an escape hatch.
 */
export function flagDefault(flag: Flag, _host: string): boolean {
  if (flag === 'lanesTab') return true
  return false
}

function readOverrides(): Partial<Record<Flag, boolean>> {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, unknown>
    const out: Partial<Record<Flag, boolean>> = {}
    for (const f of FLAGS) if (typeof v[f] === 'boolean') out[f] = v[f] as boolean
    return out
  } catch {
    return {}
  }
}

/** Applies `?flags=a,-b` to the stored overrides (once per page load, before the first read). */
export function applyFlagParam(search: string): void {
  const raw = new URLSearchParams(search).get('flags')
  if (!raw) return
  const next = readOverrides()
  for (const part of raw.split(',')) {
    const name = part.replace(/^[-+]/, '') as Flag
    if (FLAGS.includes(name)) next[name] = !part.startsWith('-')
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // ignore
  }
}

let applied = false
export function isEnabled(
  flag: Flag,
  loc: Pick<Location, 'hostname' | 'search'> = location,
): boolean {
  if (!applied) {
    applied = true
    applyFlagParam(loc.search)
  }
  return readOverrides()[flag] ?? flagDefault(flag, loc.hostname)
}
